import React from 'react';
import { Box, Button, CircularProgress, FormHelperText, Grid, Stack } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ObjectSchema } from 'yup';

import { CustomerNameInput, GenderButtonGroup, OutlinedInput, PhoneNumberInput } from '../subcomponents';
import { isRequired } from '../util/isRequired';

type NoteError = {
  message: string;
  onRetry: () => void;
};

type Props = {
  size?: 'default' | 'small';
  isDisabled?: boolean;
  schema: ObjectSchema<any>;
  noteLoading?: boolean;
  noteError?: NoteError | null;
};

function AppointmentFormCustomerSectionComponent({ size, isDisabled, schema, noteLoading, noteError }: Props) {
  const form = useFormContext();
  const intl = useIntl();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Controller
          control={form.control}
          name="gender"
          render={({ field }) => (
            <GenderButtonGroup
              disabled={!!isDisabled}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Grid>

      <Grid item xs={size === 'small' ? 12 : 6}>
        <Controller
          control={form.control}
          name="lastName"
          render={({ field, fieldState }) => (
            <CustomerNameInput
              dataTestId="lastname-input"
              placeholder={intl.formatMessage({ id: 'appointment.lastName' })}
              disabled={!!isDisabled}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              required={isRequired(schema, field.name)}
              error={!!fieldState.error}
            />
          )}
        />
      </Grid>

      <Grid item xs={size === 'small' ? 12 : 6}>
        <Controller
          control={form.control}
          name="firstName"
          render={({ field, fieldState }) => (
            <CustomerNameInput
              dataTestId="firstname-input"
              placeholder={intl.formatMessage({ id: 'appointment.firstName' })}
              disabled={!!isDisabled}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              required={isRequired(schema, field.name)}
              error={!!fieldState.error}
            />
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <CustomerNameInput
              dataTestId="email-input"
              placeholder={intl.formatMessage({ id: 'appointment.email' })}
              disabled={!!isDisabled}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              required={isRequired(schema, field.name)}
              error={!!fieldState.error}
            />
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          control={form.control}
          name="phoneNumber"
          render={({ field, fieldState }) => (
            <PhoneNumberInput
              dataTestId="phonenumber-input"
              placeholder={intl.formatMessage({ id: 'appointment.phoneNumber' })}
              disabled={!!isDisabled}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              required={isRequired(schema, field.name)}
              error={!!fieldState.error}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Box position="relative">
          <Controller
            control={form.control}
            name="note"
            render={({ field, fieldState }) => (
              <OutlinedInput
                dataTestId="customer-notes-input"
                placeholder={intl.formatMessage({ id: 'appointment.notes' })}
                disabled={!!isDisabled || !!noteLoading || !!noteError}
                value={field.value || ''}
                multiline
                onChange={field.onChange}
                onBlur={field.onBlur}
                required={isRequired(schema, field.name)}
                error={!!fieldState.error}
              />
            )}
          />
          {noteLoading && (
            <Box
              data-testid="customer-notes-loading"
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ pointerEvents: 'none' }}
            >
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>
        {noteError && (
          <Stack direction="row" alignItems="center" spacing={1} mt={0.5} data-testid="customer-notes-error">
            <FormHelperText error sx={{ m: 0 }}>{noteError.message}</FormHelperText>
            <Button size="small" variant="text" onClick={noteError.onRetry}>
              {intl.formatMessage({ id: 'common.retry' })}
            </Button>
          </Stack>
        )}
      </Grid>
    </Grid>
  );
}

AppointmentFormCustomerSectionComponent.defaultProps = {
  size: 'default',
  isDisabled: false,
  noteLoading: false,
  noteError: null,
};

export const AppointmentFormCustomerSection = React.memo(AppointmentFormCustomerSectionComponent);
